import React, {memo, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {Button, Popconfirm, Typography} from 'antd';
import {DeleteOutlined, ApiOutlined} from '@ant-design/icons';
import {Virtuoso} from 'react-virtuoso';

import {OpenApiContext} from '../../openapi-context';
import {AppContext} from '../../state';

// Flat virtualized list of webhooks. OpenAPI 3.1 only — the parent view
// decides whether to render this component at all based on
// spec.openapi.startsWith('3.1'). Structure mirrors api-index.js because
// webhooks and paths are both PathItems; only the container key differs
// ('paths' -> 'webhooks') and the display "method" concept is simpler
// (webhooks are almost always POST, but we still reflect what's declared).

const SUPPORTED_METHODS = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options', 'trace'];

function buildEntries(webhooks) {
    if (!webhooks) return [];
    return webhooks.map(({key, name, spec}) => {
        const methods = Object.keys(spec || {}).filter((m) => SUPPORTED_METHODS.indexOf(m) !== -1);
        return {
            key,
            name,
            method: methods.length === 1 ? methods[0] : methods,
        };
    });
}

const WebhookRow = memo(function WebhookRow({entry, isActive, href, onNavigate, onDelete, editMode}) {
    return (
        <div className={`apiList__row${isActive ? ' is-active' : ''}`}>
            <span className="apiList__method">
                <ApiOutlined style={{color: 'rgba(0,0,0,0.45)'}} />
            </span>
            <Link className="apiList__path" disabled={editMode} onClick={onNavigate} to={href}>
                {entry.name}
            </Link>
            <Popconfirm okType="default" title={`Delete webhook ${entry.name} ?`} onConfirm={() => onDelete(entry)}>
                <Button
                    className="apiList__delete"
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                />
            </Popconfirm>
        </div>
    );
});

export function WebhookIndex({currentHash, filter, onDelete, params, checkMobileView}) {
    const {dispatch} = useContext(AppContext);
    const {state} = useContext(OpenApiContext);

    const [webhooks, setWebhooks] = useState([]);

    useEffect(() => {
        if (state.webhooks) setWebhooks(state.webhooks(filter));
    }, [state.webhooks, filter, currentHash]);

    const entries = useMemo(() => buildEntries(webhooks), [webhooks]);

    const onNavigate = useCallback(() => {
        if (checkMobileView) checkMobileView();
        dispatch({type: 'clear-breadcrumb'});
    }, [checkMobileView, dispatch]);

    const onDeleteEntry = useCallback(
        (entry) => {
            onDelete({...entry, name: entry.name});
        },
        [onDelete],
    );

    const activeKey = params && params.view === 'webhooks' ? params.intfKey : null;

    const itemContent = useCallback(
        (index) => {
            const entry = entries[index];
            const href = `/${params.env}/standards/${params.category}/${params.key}/${params.version}/webhooks/${entry.key}`;
            return (
                <WebhookRow
                    entry={entry}
                    isActive={activeKey === entry.key}
                    href={href}
                    onNavigate={onNavigate}
                    onDelete={onDeleteEntry}
                    editMode={state.editMode}
                />
            );
        },
        [entries, params, activeKey, onNavigate, onDeleteEntry, state.editMode],
    );

    if (entries.length === 0) {
        return (
            <div className="webhookEmpty">
                <Typography.Text type="secondary">No webhooks defined on this spec yet.</Typography.Text>
            </div>
        );
    }

    return (
        <div className="apiList">
            <Virtuoso
                className="apiList__virtuoso"
                totalCount={entries.length}
                itemContent={itemContent}
                computeItemKey={(index) => entries[index]?.key || index}
                increaseViewportBy={{top: 200, bottom: 200}}
                overscan={10}
            />
        </div>
    );
}
